import React, { useEffect, useMemo, useRef } from "react";
import type { MapRef } from "react-map-gl";
import useAxios from "../../utils/useAxios";
import { IndexName, isValidValue, satelliteColor } from "../../utils/satelliteProducts";
import { useToast } from "@chakra-ui/react";

export type { IndexName } from "../../utils/satelliteProducts";

export interface SatelliteEntry {
  satellite_data_id: number;
  request_id: number;
  created_at: string;
  ingestion_date: string | null;
  acquisition_date?: string | null;
  indices: Record<string, string>;
  download_urls: Record<string, string>;
}

const CANVAS_SOURCE_ID = "satellite-canvas-src";
const CANVAS_LAYER_ID = "satellite-canvas-layer";

type Props = {
  mapRef: React.RefObject<MapRef>;
  enabled: boolean;
  indexName: IndexName;
  valueRange: [number, number];
  opacity: number;
  entries: SatelliteEntry[];
  selectedId: number | null;
  applyToken: number;
  onLoadingChange?: (loading: boolean) => void;
};

const RenderSatelliteData: React.FC<Props> = ({
  mapRef,
  enabled,
  indexName,
  valueRange,
  opacity,
  entries,
  selectedId,
  applyToken,
  onLoadingChange,
}) => {
  const api = useAxios();
  const toast = useToast();
  const onLoadingChangeRef = useRef(onLoadingChange);
  onLoadingChangeRef.current = onLoadingChange;
  const displaySettingsRef = useRef({ indexName, valueRange, opacity });
  displaySettingsRef.current = { indexName, valueRange, opacity };
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const rasterRef = useRef<{
    band: Float32Array | Uint16Array | Int16Array | Uint8Array;
    width: number;
    height: number;
    noDataVal?: number;
  } | null>(null);

  const selectedEntry = useMemo(() => {
    const available = entries.filter((entry) => entry.download_urls[indexName]);
    return available.find((entry) => entry.satellite_data_id === selectedId) || available[0];
  }, [entries, selectedId, indexName]);

  const selectedTiffUrl = selectedEntry?.download_urls?.[indexName];

  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map) return;

    let cancelled = false;

    const removeLayer = () => {
      if (map.getLayer(CANVAS_LAYER_ID)) map.removeLayer(CANVAS_LAYER_ID);
      if (map.getSource(CANVAS_SOURCE_ID)) map.removeSource(CANVAS_SOURCE_ID);
    };

    const run = async () => {
      if (!enabled || !selectedTiffUrl) {
        onLoadingChangeRef.current?.(false);
        removeLayer();
        return;
      }
      onLoadingChangeRef.current?.(true);
      try {
        const geotiff = await import("geotiff");
        const res = await api.get<ArrayBuffer>(selectedTiffUrl, {
          responseType: "arraybuffer",
        });
        const buf = res.data;
        const tiff = await geotiff.fromArrayBuffer(buf);
        const image = await tiff.getImage();
        const bbox = image.getBoundingBox();
        const width = image.getWidth();
        const height = image.getHeight();
        const rasters = await image.readRasters({ samples: [0], interleave: false });
        if (cancelled) return;
        const band = rasters[0] as Float32Array | Uint16Array | Int16Array | Uint8Array;
        const display = displaySettingsRef.current;

        const canvas =
          canvasRef.current ||
          (document.getElementById("satellite-canvas") as HTMLCanvasElement) ||
          document.createElement("canvas");
        canvas.id = "satellite-canvas";
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        if (!ctx) throw new Error("Canvas 2D not supported");
        const imgData = ctx.createImageData(width, height);

        const noDataRaw =
          (image as any).fileDirectory?.GDAL_NODATA ?? (image as any).getGDALNoData?.();
        const noDataVal: number | undefined =
          typeof noDataRaw === "number"
            ? noDataRaw
            : typeof noDataRaw === "string" && Number.isFinite(parseFloat(noDataRaw))
              ? parseFloat(noDataRaw)
              : undefined;

        rasterRef.current = { band, width, height, noDataVal };
        const [min, max] = display.valueRange;

        for (let i = 0; i < band.length; i++) {
          const vRaw = (band as any)[i];
          const v = typeof vRaw === "number" ? vRaw : Number(vRaw);
          const j = i * 4;

          if (!isValidValue(v, display.indexName, noDataVal)) {
            imgData.data[j] = 0;
            imgData.data[j + 1] = 0;
            imgData.data[j + 2] = 0;
            imgData.data[j + 3] = 0;
            continue;
          }
          const [rC, gC, bC, aC] = satelliteColor(display.indexName, v, min, max);

          imgData.data[j] = rC;
          imgData.data[j + 1] = gC;
          imgData.data[j + 2] = bC;
          imgData.data[j + 3] = aC;
        }
        ctx.putImageData(imgData, 0, 0);
        map.triggerRepaint();
        canvasRef.current = canvas;
        if (cancelled) return;

        const [minX, minY, maxX, maxY] = bbox;
        const cornersSrc: [number, number][] = [
          [minX, maxY],
          [maxX, maxY],
          [maxX, minY],
          [minX, minY],
        ];

        let transformXY = (x: number, y: number): [number, number] => [x, y];
        try {
          const geoKeys: any = (image as any).getGeoKeys?.();
          const projCode: number | undefined =
            geoKeys?.ProjectedCSTypeGeoKey || geoKeys?.GeographicTypeGeoKey;

          const toWgsFrom3857 = (x: number, y: number): [number, number] => {
            const R = 6378137;
            const lon = (x / R) * (180 / Math.PI);
            const lat = (2 * Math.atan(Math.exp(y / R)) - Math.PI / 2) * (180 / Math.PI);
            return [lon, lat];
          };
          const utmDefFromEpsg = (code: number) => {
            if (code >= 32601 && code <= 32660) {
              const zone = code - 32600;
              return {
                def: `+proj=utm +zone=${zone} +datum=WGS84 +units=m +no_defs +type=crs`,
                name: `EPSG:${code}`,
              };
            }
            if (code >= 32701 && code <= 32760) {
              const zone = code - 32700;
              return {
                def: `+proj=utm +zone=${zone} +south +datum=WGS84 +units=m +no_defs +type=crs`,
                name: `EPSG:${code}`,
              };
            }
            return null;
          };

          if (projCode === 4326) transformXY = (x, y) => [x, y];
          else if (projCode === 3857 || projCode === 102100 || projCode === 900913)
            transformXY = toWgsFrom3857;
          else if (typeof projCode === "number") {
            try {
              const proj4mod: any = await import("proj4");
              const proj4 = proj4mod.default || proj4mod;
              const utm = utmDefFromEpsg(projCode);
              if (utm) {
                if (!proj4.defs[utm.name]) proj4.defs(utm.name, utm.def);
                transformXY = (x, y) => {
                  const out = proj4(utm.name, "EPSG:4326", [x, y]);
                  return [out[0], out[1]];
                };
              } else {
                const from = `EPSG:${projCode}`;
                transformXY = (x, y) => {
                  const out = proj4(from, "EPSG:4326", [x, y]);
                  return [out[0], out[1]];
                };
              }
            } catch {
              const guessMerc =
                Math.max(Math.abs(minX), Math.abs(minY), Math.abs(maxX), Math.abs(maxY)) > 180;
              transformXY = guessMerc ? toWgsFrom3857 : (x, y) => [x, y];
            }
          } else {
            const guessMerc =
              Math.max(Math.abs(minX), Math.abs(minY), Math.abs(maxX), Math.abs(maxY)) > 180;
            transformXY = guessMerc ? toWgsFrom3857 : (x, y) => [x, y];
          }
        } catch (e) {
          console.error(e);
        }

        const coordinates = cornersSrc.map(([x, y]) => transformXY(x, y)) as [number, number][];
        if (coordinates.some(([, lat]) => lat < -90 || lat > 90)) {
          const toWgsFrom3857 = (x: number, y: number): [number, number] => {
            const R = 6378137;
            const lon = (x / R) * (180 / Math.PI);
            const lat = (2 * Math.atan(Math.exp(y / R)) - Math.PI / 2) * (180 / Math.PI);
            return [lon, lat];
          };
          const fallback = cornersSrc.map(([x, y]) => toWgsFrom3857(x, y)) as [number, number][];
          if (!fallback.some(([, lat]) => lat < -90 || lat > 90)) {
            coordinates.splice(0, coordinates.length, ...fallback);
          }
        }
        const ensure = () => {
          if (cancelled) return;
          const src: any = map.getSource(CANVAS_SOURCE_ID);
          if (!src) {
            // Pass the actual canvas element instead of the ID string to avoid DOM lookup nulls
            map.addSource(CANVAS_SOURCE_ID, {
              type: "canvas",
              canvas: canvas,
              coordinates: coordinates,
              animate: true,
            } as any);
            if (!map.getLayer(CANVAS_LAYER_ID)) {
              map.addLayer({
                id: CANVAS_LAYER_ID,
                type: "raster",
                source: CANVAS_SOURCE_ID,
                paint: { "raster-opacity": displaySettingsRef.current.opacity },
              });
            }
          } else {
            src.setCoordinates(coordinates);
          }
        };
        if (map.isStyleLoaded()) ensure();
        else map.once("load", ensure);
      } catch (e) {
        if (cancelled) return;
        console.error(e);
        toast({ title: "Failed to render satellite data", status: "error", isClosable: true });
      } finally {
        if (!cancelled) onLoadingChangeRef.current?.(false);
      }
    };

    run();
    return () => {
      cancelled = true;
      const map2 = mapRef.current?.getMap();
      if (!map2) return;
      if (!enabled) {
        if (map2.getLayer(CANVAS_LAYER_ID)) map2.removeLayer(CANVAS_LAYER_ID);
        if (map2.getSource(CANVAS_SOURCE_ID)) map2.removeSource(CANVAS_SOURCE_ID);
      }
    };
    // api is a configured Axios instance recreated by the auth hook.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, selectedTiffUrl, applyToken]);

  useEffect(() => {
    if (!enabled) return;
    const map = mapRef.current?.getMap();
    const r = rasterRef.current;
    const canvas =
      canvasRef.current ||
      (document.getElementById("satellite-canvas") as HTMLCanvasElement | null);
    if (!map || !r || !canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const { band, width, height, noDataVal } = r;
    const imgData = ctx.createImageData(width, height);
    const [min, max] = valueRange;

    for (let i = 0; i < band.length; i++) {
      const vRaw = (band as any)[i];
      const v = typeof vRaw === "number" ? vRaw : Number(vRaw);
      const j = i * 4;
      if (!isValidValue(v, indexName, noDataVal)) {
        imgData.data[j] = 0;
        imgData.data[j + 1] = 0;
        imgData.data[j + 2] = 0;
        imgData.data[j + 3] = 0;
        continue;
      }
      const [rC, gC, bC, aC] = satelliteColor(indexName, v, min, max);
      imgData.data[j] = rC;
      imgData.data[j + 1] = gC;
      imgData.data[j + 2] = bC;
      imgData.data[j + 3] = aC;
    }
    ctx.putImageData(imgData, 0, 0);
    map.triggerRepaint();
  }, [valueRange, indexName, enabled]);

  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map || !enabled) return;
    if (map.getLayer(CANVAS_LAYER_ID)) {
      try {
        map.setPaintProperty(CANVAS_LAYER_ID, "raster-opacity", opacity);
      } catch {}
    }
  }, [opacity, enabled, mapRef]);

  useEffect(() => {
    return () => {
      const map = mapRef.current?.getMap();
      if (!map) return;
      if (map.getLayer(CANVAS_LAYER_ID)) map.removeLayer(CANVAS_LAYER_ID);
      if (map.getSource(CANVAS_SOURCE_ID)) map.removeSource(CANVAS_SOURCE_ID);
    };
  }, [mapRef]);

  return null;
};

export default RenderSatelliteData;
